import { IconButton } from '@atlaskit/button/new';
import CrossCircleIcon from '@atlaskit/icon/core/cross-circle';
import SearchIcon from '@atlaskit/icon/core/search';
import { Flex, xcss } from '@atlaskit/primitives';
import Textfield from '@atlaskit/textfield';
import Tooltip from '@atlaskit/tooltip';
import { useFlaggingCallback } from '@databases/banners';
import { messages } from '@databases/intl/messages';
import {
  KeyboardShortcut,
  useDebouncedCallback,
  useKeyboardShortcut,
} from '@databases/react';
import { DatabaseViewSettings } from '@databases/types/database/view';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl-next';
import styled, { css } from 'styled-components';

import { isApple } from '../../../utils';

export interface QuickSearchProps {
  value?: DatabaseViewSettings['search'];
  onChange: (value: DatabaseViewSettings['search']) => void;
}

const searchIconWrapperStyles = xcss({
  paddingInlineStart: 'space.100',
});

export const SearchFieldTransition = styled.div<{ expanded: boolean }>`
  transition: width 0.3s, background 0.3s;

  ${(props) =>
    props.expanded
      ? css`
          width: 382px;
        `
      : css`
          width: 32px;
        `}
`;

const findShortcut: KeyboardShortcut = {
  modifiers: [isApple ? 'metaKey' : 'ctrlKey'],
  code: 'KeyF',
};
const blurShortcut: KeyboardShortcut = { code: 'Escape' };
const shortCutTooltipDelay = 1000;

const QuickSearch: FunctionComponent<QuickSearchProps> = ({
  onChange: onChangeProp,
  value: valueProp,
}) => {
  const [value, setLocalValue] = useState(valueProp);
  const debouncedOnChanged = useDebouncedCallback(onChangeProp, 500);
  const isEmpty = !value;

  /**
   * The local value should be updated immediately, but the callback
   * to trigger the search should be debounced.
   */
  const updateValue = useCallback(
    (value?: string) => {
      setLocalValue(value);
      debouncedOnChanged(value);
    },
    [setLocalValue, debouncedOnChanged]
  );

  /**
   * Sync local search field state with external changes to current
   * view settings (changing views, etc)
   */
  useEffect(() => {
    setLocalValue(valueProp);
    if (!valueProp) {
      setExpanded(false);
    }
  }, [valueProp]);

  const searchFieldRef = useRef<HTMLInputElement>(null);

  const [expanded, setExpanded] = useState(!isEmpty);

  const { formatMessage } = useIntl();

  const handleExpandField = useCallback(() => {
    setExpanded(true);
  }, []);

  const handleFocus = useCallback(() => {
    searchFieldRef.current?.select();
  }, []);

  useKeyboardShortcut(!expanded ? findShortcut : undefined, handleExpandField);
  useKeyboardShortcut(expanded && !isEmpty ? findShortcut : undefined, handleFocus);

  const handleBlur = useCallback(() => {
    isEmpty && setExpanded(false);
  }, [isEmpty]);

  const handleEscape = useCallback(() => {
    searchFieldRef.current?.blur();
  }, []);

  useKeyboardShortcut(expanded ? blurShortcut : undefined, handleEscape);

  const handleClear = useCallback(() => {
    updateValue(undefined);
    searchFieldRef.current?.focus();
  }, [updateValue]);

  const handleChange = useFlaggingCallback(
    (e: React.FormEvent) => {
      if (!expanded) {
        handleExpandField();
      }

      const value = (e.target as HTMLInputElement).value;

      updateValue(value);
    },
    [expanded, handleExpandField, updateValue]
  );

  const clearButton = (
    <IconButton
      appearance="subtle"
      icon={CrossCircleIcon}
      label={formatMessage(messages['quick-search.clear-label'])}
      onClick={handleClear}
    />
  );

  return (
    <SearchFieldTransition expanded={expanded}>
      {expanded ? (
        <Textfield
          ref={searchFieldRef}
          autoComplete="off"
          autoFocus
          placeholder={formatMessage(messages['quick-search.search-database-text'])}
          onChange={handleChange}
          onBlur={handleBlur}
          value={value ?? ''}
          elemAfterInput={isEmpty ? undefined : clearButton}
          elemBeforeInput={
            <Flex xcss={searchIconWrapperStyles}>
              <SearchIcon label="" spacing="spacious" />
            </Flex>
          }
          label={formatMessage(messages['quick-search.search-database-text'])}
          testId="database-search"
        />
      ) : (
        <Tooltip
          hideTooltipOnClick
          delay={shortCutTooltipDelay}
          content={
            <>
              {formatMessage(messages['quick-search.quick-search-tooltip-text'])}{' '}
              <kbd>
                {isApple
                  ? formatMessage(messages['quick-search.mac-modifier'])
                  : formatMessage(messages['quick-search.ctrl'])}
              </kbd>{' '}
              {formatMessage(messages['quick-search.plus-key'])}{' '}
              <kbd>{formatMessage(messages['quick-search.F-key'])}</kbd>
            </>
          }
        >
          <IconButton
            icon={SearchIcon}
            label={formatMessage(messages['quick-search.search-database-text'])}
            onClick={handleExpandField}
          />
        </Tooltip>
      )}
    </SearchFieldTransition>
  );
};

export default QuickSearch;
